import { useState } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { Check, ChevronsUpDown } from 'lucide-react';

import { STATES_DATA } from '@/lib/states';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './form';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

interface StateComboBoxProps {
  control: Control<FieldValues>;
  disabled?: boolean;
}

export function StateComboBox({ control, disabled }: StateComboBoxProps) {
  const [open, setOpen] = useState(false);

  return (
    <FormField
      control={control}
      name='refund_to_state'
      render={({ field }) => (
        <FormItem>
          <FormLabel className='font-medium'>State</FormLabel>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant='outline'
                  role='combobox'
                  disabled={disabled}
                  className={`w-full justify-between border-border bg-input font-normal text-foreground ${!field.value && 'text-muted-foreground'}`}
                >
                  {field.value
                    ? STATES_DATA.filter(
                        (state) => state.abbreviation === field.value,
                      )?.map(
                        (state) => state.name + ' (' + state.abbreviation + ')',
                      )
                    : 'Select state'}
                  <ChevronsUpDown />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className='max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] p-0'>
              <Command>
                <CommandInput placeholder='Search state...' className='h-9' />
                <CommandList>
                  <CommandEmpty>No state found.</CommandEmpty>
                  <CommandGroup>
                    {STATES_DATA.map((state) => (
                      <CommandItem
                        value={state.name + ' (' + state.abbreviation + ')'}
                        key={state.abbreviation}
                        onSelect={() => {
                          field.onChange(state.abbreviation);
                          setOpen(false);
                        }}
                      >
                        {state.name + ' (' + state.abbreviation + ')'}
                        <Check
                          className={`ml-auto ${state.abbreviation === field.value ? 'opacity-100' : 'opacity-0'}`}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
